import {
  ContractTagSuggestRequestSchema,
  ENDPOINTS,
  ManageContractTagsRequestSchema,
  type ContractTagSuggestRequest,
  type ContractTagSuggestResponse,
  type ManageContractTagsRequest,
  type ManageContractTagsResponse,
} from '@clm/shared-types';

import { apiGet, apiPost, type Query } from './client';
import { substitutePath } from './verifications';

/**
 * `manage-contract-tags` and `contract-tags-suggest` — CLM-77, R-77-1.
 *
 * Same recipe as `clause-matches.ts`: the path comes from the registry, the
 * request is `.parse`d before it leaves the browser (both request schemas are
 * `.strict()`), and the response is **parsed** rather than asserted so an
 * off-contract answer is a defect to report, not data to show.
 *
 * The browser sends the tags already normalised (`normaliseTag` from
 * `@clm/shared-types`, the one normaliser); the API re-normalises and 400s on
 * anything else. The replacement set is scoped server-side on
 * `root_contract_key` (NFR-06) — an out-of-scope key is a 404 here, never a 403.
 */

const MANAGE_ENDPOINT = ENDPOINTS['manage-contract-tags'];
const SUGGEST_ENDPOINT = ENDPOINTS['contract-tags-suggest'];

/** POST the replacement set (D-77-5). One `Update` audit row per call, whatever changed. */
export async function manageContractTags(
  request: ManageContractTagsRequest,
  signal?: AbortSignal,
): Promise<ManageContractTagsResponse> {
  const validated = ManageContractTagsRequestSchema.parse(request);
  const path = substitutePath(MANAGE_ENDPOINT.path, { contract_key: validated.contract_key });
  const raw = await apiPost<unknown>(
    path,
    { tags: validated.tags },
    signal === undefined ? undefined : { signal },
  );
  return MANAGE_ENDPOINT.response.parse(raw);
}

/** Flatten a validated suggest request into the query the decoder expands. */
export function contractTagSuggestQuery(request: ContractTagSuggestRequest): Query {
  const query: Record<string, string | number> = {};
  if (request.q !== undefined) query['q'] = request.q;
  if (request.limit !== undefined) query['limit'] = request.limit;
  return query;
}

/** GET the tags already in the caller's scope that start with `q`, most-used first. */
export async function suggestContractTags(
  request: ContractTagSuggestRequest,
  signal?: AbortSignal,
): Promise<ContractTagSuggestResponse> {
  const validated = ContractTagSuggestRequestSchema.parse(request);
  const raw = await apiGet<unknown>(SUGGEST_ENDPOINT.path, {
    query: contractTagSuggestQuery(validated),
    ...(signal === undefined ? {} : { signal }),
  });
  return SUGGEST_ENDPOINT.response.parse(raw);
}
